/**
 * ConfirmDialog + useConfirm — institutional confirmation primitive.
 *
 * Replaces the native `window.confirm()` which breaks the premium visual
 * coherence. One sober, non-chatty modal, explicit action label, loading-safe
 * (double-click guarded), tone-aware (neutral / warning / danger).
 *
 * Two supported usage patterns:
 *
 *  (1) Declarative — render the modal yourself, pass `open` + handlers.
 *      const [askDel, setAskDel] = useState(false);
 *      <ConfirmDialog
 *        open={askDel}
 *        title="Supprimer l'événement ?"
 *        body="Cette action est définitive."
 *        confirmLabel="Supprimer"
 *        tone="danger"
 *        onConfirm={async () => { await doDelete(); setAskDel(false); }}
 *        onCancel={() => setAskDel(false)}
 *      />
 *
 *  (2) Imperative hook — returns a promise that resolves true/false.
 *      const { confirm, confirmNode } = useConfirm();
 *      const ok = await confirm({
 *        title: 'Supprimer ?',
 *        body: 'Irréversible.',
 *        confirmLabel: 'Supprimer',
 *        tone: 'danger',
 *      });
 *      if (ok) { ... }
 *      // render {confirmNode} once somewhere in the tree.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Warning, ShieldCheck, X } from '@phosphor-icons/react';

const TONE_MAP = {
  neutral: {
    accent:   'text-bronze',
    border:   'border-bronze/60',
    btnClass: 'bg-charcoal hover:bg-deep-green text-ivory',
    icon:     ShieldCheck,
  },
  warning: {
    accent:   'text-warning',
    border:   'border-warning/70',
    btnClass: 'bg-warning hover:bg-warning/90 text-ivory',
    icon:     Warning,
  },
  danger: {
    accent:   'text-danger',
    border:   'border-danger/70',
    btnClass: 'bg-danger hover:bg-danger/90 text-ivory',
    icon:     Warning,
  },
};


export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirmer',
  cancelLabel  = 'Annuler',
  tone         = 'neutral',
  overline     = 'Confirmation requise',
  onConfirm,
  onCancel,
  testid       = 'confirm-dialog',
}) {
  const [busy, setBusy] = useState(false);
  const confirmRef      = useRef(null);
  const toneCfg         = TONE_MAP[tone] || TONE_MAP.neutral;
  const Icon            = toneCfg.icon;

  // Focus confirm button when opened.
  useEffect(() => {
    if (open) {
      setBusy(false);
      // Defer so the DOM is ready.
      setTimeout(() => confirmRef.current?.focus(), 10);
    }
  }, [open]);

  // ESC to cancel, Enter to confirm.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (busy) return;
      if (e.key === 'Escape') { e.preventDefault(); onCancel?.(); }
      else if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy]);

  const handleConfirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      // Parent is expected to close; keep busy true to prevent double-click.
      // If parent didn't close within 2s, release the guard.
      setTimeout(() => setBusy(false), 2000);
    }
  }, [busy, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testid}-title`}
      onClick={() => !busy && onCancel?.()}
      data-testid={testid}
    >
      <div
        className="bg-white border border-stone-300 max-w-md w-full p-6 lining-nums tabular-nums shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Icon size={16} weight="duotone" className={toneCfg.accent} />
            <p className={`text-[10px] uppercase tracking-[0.2em] ${toneCfg.accent}`}>{overline}</p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            className="text-graphite/80 hover:text-charcoal"
            aria-label="Fermer"
            data-testid={`${testid}-close`}
          >
            <X size={16} />
          </button>
        </div>
        <h3 id={`${testid}-title`} className="font-heading text-xl text-charcoal leading-tight mb-2">
          {title}
        </h3>
        <div className={`h-px w-12 ${toneCfg.border.replace('border-', 'bg-')} mb-4`} />
        {body && (
          <div className="text-sm text-graphite leading-relaxed mb-6" data-testid={`${testid}-body`}>
            {body}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            className="px-4 py-2 border border-stone-400 text-xs uppercase tracking-[0.12em] text-charcoal hover:bg-stone-100 transition-colors disabled:opacity-40"
            data-testid={`${testid}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`px-5 py-2 ${toneCfg.btnClass} text-xs uppercase tracking-[0.12em] transition-colors disabled:opacity-60`}
            data-testid={`${testid}-confirm`}
          >
            {busy ? 'Traitement…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * useConfirm — imperative async confirmation.
 * Returns {confirm, confirmNode}. Mount `confirmNode` once anywhere in the tree.
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolverRef       = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState({
      title:        opts.title        || 'Confirmer l\u2019action ?',
      body:         opts.body         || null,
      confirmLabel: opts.confirmLabel || 'Confirmer',
      cancelLabel:  opts.cancelLabel  || 'Annuler',
      tone:         opts.tone         || 'neutral',
      overline:     opts.overline     || 'Confirmation requise',
    });
  }), []);

  const close = useCallback((value) => {
    setState(null);
    resolverRef.current?.(value);
    resolverRef.current = null;
  }, []);

  const confirmNode = (
    <ConfirmDialog
      open={!!state}
      title={state?.title}
      body={state?.body}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      tone={state?.tone}
      overline={state?.overline}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, confirmNode };
}
